import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpStatusCode } from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';
import {Configuration} from "../../assets/Configuration";
import { PersonSummaryModel } from '../models/person/person-summary.model';
import { PersonCreationAndUpdateModel } from '../models/person/person-creation-and-update.model';
import { PersonFullModel } from '../models/person/person-full.model';
import { AddExternalCertificateModel } from '../models/person/add-external-certificate.model';
import { RawFileModel } from '../models/raw-file-model';
import {PersonSummaryWithIddModel} from "../models/person/person-summary-with-idd.model";
import { CompletedEventCategoryModel } from '../models/completed-event/completed-event-category.model';
import { LearningPathProgressModel } from '../models/dashboard/learning-path-progress.model';
import { LearningPathModel } from '../models/learning-paths/learning-path.model';
import { PersonModel } from '../models/person/person.model';
import { ActivatePersonsModel } from '../models/person/activate-persons.model';
import { PersonBasicSummaryModel } from '../models/person/person-basic-summary.model';
import { PersonFullWithEventMemberModel } from '../models/person/person-full-with-event-member.model';

@Injectable({
  providedIn: 'root',
})
export class PersonService {
  constructor(private httpClient: HttpClient) {}

  public getAllPersons(): Observable<PersonBasicSummaryModel[]> {
    return this.httpClient.get<PersonBasicSummaryModel[]>(
      Configuration.getApiBaseUrl() + '/person/'
    );
  }

  public getPersonCount(): Observable<number> {
    return this.httpClient.get<number>(
      Configuration.getApiBaseUrl() + '/person/count'
    );
  }

  public getPersonsWithoutBrokers(): Observable<PersonBasicSummaryModel[]> {
    return this.httpClient.get<PersonBasicSummaryModel[]>(
      Configuration.getApiBaseUrl() + '/person/without-brokers'
    );
  }

  public getPersonsByState(inactive: boolean): Observable<PersonSummaryWithIddModel[]> {
    const params = new HttpParams()
      .set('inactive', inactive);
    
    return this.httpClient.get<PersonSummaryWithIddModel[]>(
      Configuration.getApiBaseUrl() + '/person/by-state',
      { params: params}
    );
  }
  
  public getCurrentPersonSummaryWithIdd(): Observable<PersonSummaryWithIddModel> {
    return this.httpClient.get<PersonSummaryWithIddModel>(
      Configuration.getApiBaseUrl() + '/person/current/idd-summary'
    );
  }

  public getPersonsByIds(ids: string[]): Observable<PersonFullModel[]> {
    return this.httpClient.post<PersonFullModel[]>(
      Configuration.getApiBaseUrl() + '/person/by-ids',
      ids
    );
  }

  public getPersonsWithEventMember(
    eventId: string,
    ids: string[]
  ): Observable<PersonFullWithEventMemberModel[]> {

    return this.httpClient.post<PersonFullWithEventMemberModel[]>(
      Configuration.getApiBaseUrl() + `/person/with-event-member/${eventId}`,
      ids
    );
  }

  public getPerson(id: string): Observable<PersonFullModel> {
    return this.httpClient.get<PersonFullModel>(
      Configuration.getApiBaseUrl() + '/person/' + id
    );
  }

  public getIddResponsiblePerson(): Observable<PersonModel | null> {
    return this.httpClient.get<PersonModel>(
      Configuration.getApiBaseUrl() + '/person/idd-responsible-person'
    ).pipe(
      catchError(err => {
        if (err.status === HttpStatusCode.NotFound) {
          return of(null);
        }
        return throwError(() => err);
      })
    );
  }

  public getEventCompletedCategories(startDate: Date, endDate: Date): Observable<CompletedEventCategoryModel[]> {
    const params = {
      start: startDate.toDateString(),
      end: endDate.toDateString()
    };
    
    return this.httpClient.get<CompletedEventCategoryModel[]>(
      Configuration.getApiBaseUrl() +  `/person/competence`,
      { params }
    );
  }

  public getPersonsByLearningPathId(learningPathId: string): Observable<PersonModel[]> {
    return this.httpClient.get<PersonModel[]>(Configuration.getApiBaseUrl() +  `/person/${learningPathId}/learning-path`);
  }

  public getEventCompletedCategoriesByPersonId(personId: string): Observable<CompletedEventCategoryModel[]> {
    return this.httpClient.get<CompletedEventCategoryModel[]>(Configuration.getApiBaseUrl() +  `/person/${personId}/competence`);
  }

  public getLearningPathsAssigned(): Observable<LearningPathModel[]> {
    return this.httpClient.get<LearningPathModel[]>(Configuration.getApiBaseUrl() + `/person/learning-path`);
  }

  public hasAssignedLearningPath(): Observable<boolean> {
    return this.httpClient.get<boolean>(
      Configuration.getApiBaseUrl() + `/person/learning-paths/has-assigned`
    );
  }

  public hasAssignedComplianceCategory(): Observable<boolean> {
    return this.httpClient.get<boolean>(
      Configuration.getApiBaseUrl() + `/person/compliance-categories/has-assigned`
    );
  }

  public getLearningPathsProgress(): Observable<LearningPathProgressModel[]> {
    return this.httpClient.get<LearningPathProgressModel[]>(
        Configuration.getApiBaseUrl() + `/person/learning-path-progress`
    );
  }

  public addPerson(
    creationAndUpdateModel: PersonCreationAndUpdateModel
  ): Observable<PersonFullModel> {
    return this.httpClient.post<PersonFullModel>(
      Configuration.getApiBaseUrl() + '/person/',
      creationAndUpdateModel
    );
  }

  public updatePerson(
    editingPersonId: string,
    creationAndUpdateModel: PersonCreationAndUpdateModel
  ): Observable<PersonFullModel> {
    return this.httpClient.post<PersonFullModel>(
      Configuration.getApiBaseUrl() + '/person/' + editingPersonId,
      creationAndUpdateModel
    );
  }

  public addExternalCertificate(
    personId: string,
    model: AddExternalCertificateModel
  ): Observable<void> {
    return this.httpClient.post<void>(
      Configuration.getApiBaseUrl() + '/person/' + personId + '/external-certificate',
      model
    );
  }

  public activePerson(editingPersonId: string): Observable<PersonFullModel> {
    return this.togglePersonState(editingPersonId, 'active');
  }
  
  public activeMultiplePersons(model: ActivatePersonsModel): Observable<void> {
    return this.httpClient.post<void>(
      Configuration.getApiBaseUrl() + '/person/activate-multiple',
      model
    );
  }

  public deactivePerson(editingPersonId: string): Observable<PersonFullModel> {
    return this.togglePersonState(editingPersonId, 'inactive');
  }

  public togglePersonState(
    editingPersonId: string,
    state: string
  ): Observable<PersonFullModel> {
    return this.httpClient.post<PersonFullModel>(
      Configuration.getApiBaseUrl() + '/person/' + editingPersonId + '/' + state,
      null
    );
  }

  public importCertificates(
    file: RawFileModel
  ): Observable<PersonFullModel> {
    return this.httpClient.post<PersonFullModel>(
      Configuration.getApiBaseUrl() + '/completedevent/import-certificates',
      file
    );
  }

  public getPersonIddTimeMinutes(personId: string): Observable<number> {
    return this.httpClient.get<number>(Configuration.getApiBaseUrl() + '/person/' + personId + '/idd-time-minutes',);
  }

  public mergePerson(
    personId: string,
    targetPersonId: string
  ): Observable<PersonFullModel> {
    return this.httpClient.post<PersonFullModel>(
      Configuration.getApiBaseUrl() + '/person/' + personId + '/merge-to/' + targetPersonId,
      null
    );
  }

  public isCreatedByCurrentTenant(
    personId: string
  ): Observable<boolean> {
    return this.httpClient.get<boolean>(
      Configuration.getApiBaseUrl() + '/person/' + personId + '/created-by-current-tenant'
    );
  }
}
